// "Where" + "Access ends on" fields for a role assignment. Shared by the add
// row and the edit dialog so both speak the same rules (assignmentScope.ts).
import React from 'react';
import { RadioGroup, RadioGroupItem } from '@/features/shared/components/ui/radio-group';
import { Checkbox } from '@/features/shared/components/ui/checkbox';
import { Input } from '@/features/shared/components/ui/input';
import type { LegalEntityLite } from '@/features/payroll/types';
import { isoToDay, type ScopeForm, type ScopeFormErrors, type WhereChoice } from './assignmentScope';

interface Props {
  idPrefix: string;
  value: ScopeForm;
  onChange: (next: ScopeForm) => void;
  errors: ScopeFormErrors;
  /** Show errors only after the user tried to submit (or touched the field). */
  showErrors: boolean;
  entities: LegalEntityLite[];
  /** The company list failed to load: "Selected companies" cannot be offered. */
  entitiesError?: string | null;
  disabled?: boolean;
}

const CHOICES: { value: WhereChoice; label: string; help: string }[] = [
  {
    value: 'unscoped',
    label: 'No company limit',
    help: 'Works as roles always have — not limited to particular companies.',
  },
  {
    value: 'all',
    label: 'Every current company',
    help: 'All the companies you have today, by name. A company added later is not included until someone grants it.',
  },
  {
    value: 'selected',
    label: 'Selected companies',
    help: 'Only the companies you tick below.',
  },
];

export const AssignmentScopeFields: React.FC<Props> = ({
  idPrefix,
  value,
  onChange,
  errors,
  showErrors,
  entities,
  entitiesError,
  disabled,
}) => {
  // The last day of access may be today (it lasts through the end of the day).
  const today = isoToDay(new Date().toISOString());
  const toggle = (id: string, on: boolean) =>
    onChange({
      ...value,
      entityIds: on ? [...new Set([...value.entityIds, id])] : value.entityIds.filter((x) => x !== id),
    });

  return (
    <div className="space-y-4">
      <fieldset disabled={disabled}>
        <legend className="text-sm font-medium text-foreground">Where</legend>
        <RadioGroup
          value={value.where}
          onValueChange={(v) => onChange({ ...value, where: v as WhereChoice })}
          className="mt-2 gap-2"
          aria-label="Where this role applies"
        >
          {CHOICES.map((c) => {
            const id = `${idPrefix}-where-${c.value}`;
            return (
              <label key={c.value} htmlFor={id} className="flex items-start gap-2 cursor-pointer">
                <RadioGroupItem id={id} value={c.value} className="mt-0.5" aria-label={c.label} />
                <span>
                  <span className="block text-sm text-foreground">{c.label}</span>
                  <span className="block text-xs text-muted-foreground">{c.help}</span>
                </span>
              </label>
            );
          })}
        </RadioGroup>

        {value.where === 'selected' && (
          <div className="mt-3 ml-6">
            {entitiesError ? (
              <p role="alert" className="text-xs text-danger-strong">
                {entitiesError}
              </p>
            ) : entities.length === 0 ? (
              <p className="text-xs text-muted-foreground">No companies found for your organization.</p>
            ) : (
              <ul
                className="max-h-40 overflow-y-auto space-y-1.5"
                aria-label="Companies"
                data-testid={`${idPrefix}-entities`}
              >
                {entities.map((e) => {
                  const id = `${idPrefix}-entity-${e.id}`;
                  return (
                    <li key={e.id} className="flex items-center gap-2">
                      <Checkbox
                        id={id}
                        checked={value.entityIds.includes(e.id)}
                        onCheckedChange={(c) => toggle(e.id, c === true)}
                        aria-label={e.name}
                      />
                      <label htmlFor={id} className="text-sm text-foreground cursor-pointer">
                        {e.name}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            {showErrors && errors.entityIds && (
              <p role="alert" className="mt-1 text-xs text-danger-strong">
                {errors.entityIds}
              </p>
            )}
          </div>
        )}
      </fieldset>

      <div>
        <label htmlFor={`${idPrefix}-ends-on`} className="text-sm font-medium text-foreground">
          Access ends on <span className="font-normal text-muted-foreground">(optional)</span>
        </label>
        <div className="mt-1 max-w-[12rem]">
          <Input
            id={`${idPrefix}-ends-on`}
            type="date"
            min={today}
            value={value.endsOn}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, endsOn: e.target.value })}
          />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Access lasts through this day, then stops on its own. Leave empty for no end date.
        </p>
        {showErrors && errors.endsOn && (
          <p role="alert" className="mt-1 text-xs text-danger-strong">
            {errors.endsOn}
          </p>
        )}
      </div>
    </div>
  );
};

export default AssignmentScopeFields;
